/**
 * 使用者列表（權限管理 — 切片 1，唯讀）。
 *
 * 對應 BE GET /admin/users（分頁 + email 搜尋）。顯示各使用者所掛角色。
 * 使用者↔角色指派、帳號狀態管理留切片 3 / 切片 4。
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import { listUsers, type RbacUserListItem } from '../lib/api-client';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

/** 帳號狀態 → Pill tone。 */
function StatusPill({ status }: { status: string }): JSX.Element {
  if (status === 'active') return <Pill tone="success">啟用</Pill>;
  if (status === 'disabled' || status === 'suspended') return <Pill tone="danger">{status}</Pill>;
  return <Pill tone="neutral">{status}</Pill>;
}

export function UsersListPage(): JSX.Element {
  /** 已送出的搜尋字串（受控；page 變動時沿用） */
  const [search, setSearch] = useState('');
  /** input 即時值（按搜尋才套用） */
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin.users', search, page],
    queryFn: () => listUsers({ q: search || undefined, page, limit: 50 }),
  });

  const items: RbacUserListItem[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 50;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const applySearch = (): void => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">使用者管理</h1>
        <div className="page-head__actions">
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        系統使用者清單與其所掛角色。使用者↔角色指派、帳號啟停/解鎖留後續切片；本頁唯讀。
      </p>

      <div style={{ display: 'flex', gap: 8, margin: '0 0 12px 0' }}>
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applySearch();
          }}
          placeholder="以 email 搜尋…"
          style={{ flex: '0 0 280px', padding: 6 }}
        />
        <Button variant="primary" onClick={applySearch}>
          搜尋
        </Button>
        {search && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPage(1);
            }}
          >
            清除
          </Button>
        )}
      </div>

      {isLoading && <div>載入中…</div>}
      {error && (
        <div className="error-banner">
          {(error as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>
            提醒：此頁需 superadmin / ticketing_admin token（admin.audit_log 權限）。
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>姓名</th>
                  <th>狀態</th>
                  <th>2FA</th>
                  <th>角色</th>
                  <th>最後登入</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty-cell">
                      {search ? '查無符合的使用者' : '目前無使用者'}
                    </td>
                  </tr>
                )}
                {items.map((u) => (
                  <tr key={u.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      <Link to={`/admin/users/${u.id}`}>{u.email}</Link>
                    </td>
                    <td>{u.full_name ?? '—'}</td>
                    <td>
                      <StatusPill status={u.account_status} />
                    </td>
                    <td>
                      {u.two_factor_enabled ? (
                        <Pill tone="success">已綁定</Pill>
                      ) : (
                        <Pill tone="neutral">未綁定</Pill>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.roles.length === 0 ? (
                          <span style={{ color: 'var(--c-text-muted)', fontSize: 12 }}>
                            （無角色）
                          </span>
                        ) : (
                          u.roles.map((role) => (
                            <Pill key={role} tone="brand" dot={false}>
                              {role}
                            </Pill>
                          ))
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                      {formatDateTime(u.last_login_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="list-foot">
            <div>
              共 {total} 位使用者（第 {data.page} / {totalPages} 頁，每頁 {limit}）
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || isFetching}
              >
                上一頁
              </Button>
              <Button
                variant="secondary"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isFetching}
              >
                下一頁
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
